import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone, Calendar, Star, ShieldCheck, Award } from "lucide-react";
import { HERO_IMAGE } from "../../lib/data";
import { AppointmentForm } from "./AppointmentForm";
import useCountUp from "../../hooks/use-count-up";

export const Hero = () => {
  const portraitRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { damping: 18, stiffness: 140 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { damping: 18, stiffness: 140 });
  const badge1Y = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { damping: 22, stiffness: 120 });
  const badge1X = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { damping: 22, stiffness: 120 });
  const badge2Y = useSpring(useTransform(my, [-0.5, 0.5], [-12, 12]), { damping: 22, stiffness: 120 });
  const badge2X = useSpring(useTransform(mx, [-0.5, 0.5], [14, -14]), { damping: 22, stiffness: 120 });

  const onMove = (e) => {
    const el = portraitRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(x);
    my.set(y);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      className="relative pt-28 sm:pt-32 pb-20 overflow-hidden bg-bone"
    >
      {/* Animated gradient mesh background */}
      <div className="gradient-mesh" aria-hidden="true" />
      <div className="mesh-blob-3" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Left: Photo card with parallax tilt */}
          <motion.div
            ref={portraitRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 relative"
            style={{ perspective: 1000 }}
          >
            <motion.div
              className="relative bg-paper rounded-3xl border border-line p-3 shadow-[0_20px_50px_-20px_rgba(14,156,169,0.22)]"
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            >
              <div className="aspect-[3/4] overflow-hidden rounded-2xl bg-bone-2">
                <img
                  src={HERO_IMAGE}
                  alt="Dr. Venkatsubramaniam"
                  className="w-full h-full object-cover"
                  data-testid="hero-doctor-portrait"
                  draggable="false"
                />
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-4">
                <span className="inline-flex items-center gap-2 bg-forest text-white px-4 py-2 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap shadow-md">
                  <Award size={14} /> 15+ Years Excellence
                </span>
              </div>
            </motion.div>

            {/* Floating credential badges that drift independently */}
            <motion.div
              style={{ x: badge1X, y: badge1Y }}
              className="hidden sm:flex absolute -left-6 top-8 bg-paper border border-line rounded-2xl px-3.5 py-2.5 shadow-md items-center gap-2"
            >
              <ShieldCheck size={14} className="text-forest" />
              <div>
                <p className="text-[9px] tracking-[0.2em] uppercase text-terra font-semibold">Certified</p>
                <p className="text-[11px] font-bold text-ink leading-tight">Da Vinci · USA</p>
              </div>
            </motion.div>

            <motion.div
              style={{ x: badge2X, y: badge2Y }}
              className="hidden sm:flex absolute -right-4 bottom-16 bg-forest text-white rounded-2xl px-3.5 py-2.5 shadow-lg items-center gap-2"
            >
              <Award size={14} />
              <div>
                <p className="text-[9px] tracking-[0.2em] uppercase text-terra-soft font-semibold">Trained</p>
                <p className="text-[11px] font-bold leading-tight">Aesculap · DE</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Middle: Doctor identity */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 bg-bone-2/70 backdrop-blur-sm border border-line text-forest px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-6">
              <ShieldCheck size={14} /> Best Urologist in Chennai · Robotic Surgery Certified
            </div>

            <h1 className="font-sans font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">
              Dr. Venkat&shy;subramaniam
            </h1>

            <p className="mt-4 text-xl sm:text-2xl font-bold text-forest tracking-tight font-mono">
              M.S, D.Lap, M.Ch (Uro)
            </p>
            <p className="mt-1 text-sm sm:text-base text-ink-soft font-medium">
              Senior Urologist in Chennai · Laparoscopic, Transplant &amp; Robotic Surgeon
            </p>

            <p className="mt-6 italic text-base sm:text-lg font-medium text-ink">
              “Technology Led — Expertise Driven”
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6 max-w-md">
              <CountStat value={15} suffix="+" label="Years Excellence" />
              <CountStat value={4.9} decimals={1} label="Rating" star />
              <CountStat value={10000} suffix="+" label="Patients" />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#book"
                data-testid="hero-book-appointment-btn"
                className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
              >
                <Phone size={16} /> Book Appointment
              </a>
              <a
                href="#book"
                data-testid="hero-call-btn"
                className="btn-outline inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-paper/80 backdrop-blur"
              >
                <Calendar size={16} /> Schedule Consultation
              </a>
            </div>
          </motion.div>

          {/* Right: Inline appointment form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4"
          >
            <div className="bg-paper/85 backdrop-blur-md rounded-3xl border border-line p-7 sm:p-8 shadow-[0_30px_60px_-30px_rgba(14,156,169,0.25)]">
              <div className="text-center mb-5">
                <h3 className="font-sans font-bold text-xl text-ink">
                  Schedule Your Consultation
                </h3>
                <p className="mt-1 text-sm text-ink-soft">
                  Dr. Venkat's team will contact you shortly
                </p>
              </div>
              <AppointmentForm variant="light" testidPrefix="hero-form" compact />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const formatNumber = (n, decimals) => {
  if (decimals > 0) return Number(n).toFixed(decimals);
  if (n >= 1000) return Math.floor(n).toLocaleString();
  return Math.floor(n);
};

const CountStat = ({ value, label, star, decimals = 0, suffix = "" }) => {
  const [ref, current] = useCountUp(value, { duration: 1600, decimals });
  return (
    <div ref={ref}>
      <div className="font-mono font-bold text-2xl sm:text-3xl text-ink leading-none flex items-baseline gap-1">
        <span>{formatNumber(current, decimals)}</span>
        {suffix && <span className="text-forest">{suffix}</span>}
        {star && <Star size={16} className="fill-terra text-terra ml-0.5 self-center" />}
      </div>
      <div className="mt-1.5 text-[11px] sm:text-xs font-medium text-ink-soft tracking-wide">
        {label}
      </div>
    </div>
  );
};
